import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";

import type { MatchMetric } from "../model/MatchMetric";
import { formatGameTime } from "./formatGameTime";
import { formatPercent } from "./formatPercent";

const MINUTES_ON_AVERAGE = msg({
  id: "finding.metric.minutes_on_average",
  message: "{value} min on average",
});

const PER_10_MIN = msg({ id: "finding.metric.per_10_min", message: "{value} per 10 min" });

const ON_AVERAGE = msg({ id: "finding.metric.on_average", message: "{value} on average" });

type Format = (value: number, i18n: I18n) => string;

const whole: Format = (value, i18n) =>
  new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 0 }).format(value);
const decimal: Format = (value, i18n) =>
  new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 1 }).format(value);
const share: Format = (value, i18n) => formatPercent(value, i18n.locale);

// Как показать значение метрики: единицы из контракта `MatchMetric`.
const FORMATS: Record<MatchMetric, Format> = {
  souls_per_min: whole,
  neutral_damage_per_min: whole,
  // Бонусов в минуту меньше одного: «3,6 за 10 мин» понятнее, чем «0,36 в минуту».
  buffs_per_min: (value, i18n) =>
    i18n._({ ...PER_10_MIN, values: { value: decimal(value * 10, i18n) } }),
  first_buff_time: (value) => formatGameTime(value),
  cs_share: share,
  hero_damage_per_min: whole,
  accuracy: share,
  crit_rate: share,
  objective_damage_per_min: whole,
  kill_participation: share,
  parry_rate: share,
  damage_taken_per_min: whole,
  time_dead_share: share,
  unspent_souls: (value, i18n) =>
    i18n._({ ...MINUTES_ON_AVERAGE, values: { value: decimal(value, i18n) } }),
  unspent_ap: (value, i18n) => i18n._({ ...ON_AVERAGE, values: { value: decimal(value, i18n) } }),
};

// Значение метрики в её единицах: «1 526», «21 %», «3,6 за 10 мин», «8:03».
export const formatMetric = (metric: MatchMetric, value: number, i18n: I18n): string =>
  FORMATS[metric](value, i18n);
